/*
 * PROD-7828 R9 — pure adapters: goserver's UploadRun / ApplyResponse / run summaries (the contract of
 * record, replayed from the synced goldens in adapters.test.js) -> what the upload panel renders.
 * Affordances come only from the server's can_apply / apply_blocked_reason (hridaya guardrail).
 */
import { EM_DASH, fmt, fmt_count, month_label, num } from '../../format';
import { who_view } from '../adapters';
import { MATCHED_BY, ROW_CODE, RUN_STATUS, UPLOAD_LIMITS, VERDICT } from './params';

export const plural = (n, one, many = `${one}s`) => `${fmt_count(n)} ${n === 1 ? one : many}`;

// Rupees as typed in the file: whole rupees unless the cell carried paise.
const amount_text = (amount) => {
  if (amount === null || amount === undefined || amount === '') return EM_DASH;
  const n = num(amount);
  return fmt(n, Number.isInteger(n) ? 0 : 2);
};

export const VERDICT_VIEW = {
  [VERDICT.VALID]: { label: 'Valid', tone: 'green' },
  [VERDICT.NEEDS_CONFIRMATION]: { label: 'Needs confirmation', tone: 'orange' },
  [VERDICT.INVALID]: { label: 'Invalid', tone: 'red' }
};

export const RUN_STATUS_VIEW = {
  [RUN_STATUS.VALIDATED]: { label: 'Preview', tone: 'blue' },
  [RUN_STATUS.APPLIED]: { label: 'Applied', tone: 'green' },
  [RUN_STATUS.DISCARDED]: { label: 'Discarded', tone: 'gray' }
};

const MATCHED_BY_TEXT = {
  [MATCHED_BY.EMAIL]: 'matched by email',
  [MATCHED_BY.UUID]: 'matched by uuid'
};

// Stand-ins for a row code that came without a message; the server's own row message wins.
export const ROW_CODE_TEXT = {
  [ROW_CODE.INVALID_REQUEST]: 'A required value is missing or malformed.',
  [ROW_CODE.INVALID_PAY_MONTH]: 'The payment month is not valid.',
  [ROW_CODE.PAY_MONTH_OUTSIDE_WINDOW]:
    'Only the current and the previous payment month can take an entry.',
  [ROW_CODE.UNKNOWN_CATEGORY]: 'Unknown category code.',
  [ROW_CODE.SYSTEM_CATEGORY_NOT_SELECTABLE]: 'That category is written by the system only.',
  [ROW_CODE.ENTRY_TYPE_NOT_ALLOWED]: 'Only additions can be uploaded.',
  [ROW_CODE.INVALID_AMOUNT]: 'Enter a positive amount in rupees with at most 2 decimals.',
  [ROW_CODE.AMOUNT_REQUIRED]: 'Amount is required.',
  [ROW_CODE.AMOUNT_ABOVE_CAP]: 'Above the per-entry maximum.',
  [ROW_CODE.INVALID_ENTRY_DATE]: 'Enter the date as YYYY-MM-DD (and the time as HH:MM, if any).',
  [ROW_CODE.NOTE_REQUIRED]: 'A note is required; the teacher sees it on the payment email.',
  [ROW_CODE.INVALID_REFERENCE_ID]: 'Reference ids use letters, digits and . _ : / - only.',
  [ROW_CODE.REFERENCE_ID_REQUIRED]: 'A reference id is required on every uploaded row.',
  [ROW_CODE.RESERVED_REFERENCE_ID]: 'The CF: prefix is reserved for system carry-forwards.',
  [ROW_CODE.TEACHER_NOT_FOUND]: 'No teacher matches this row.',
  [ROW_CODE.TEACHER_EMAIL_AMBIGUOUS]: 'More than one teacher has this email. Use teacher_uuid.',
  [ROW_CODE.TEACHER_ID_MISMATCH]: 'teacher_uuid and teacher_email name different teachers.',
  [ROW_CODE.SESSION_NOT_FOUND]: 'The related session was not found.',
  [ROW_CODE.SESSION_NOT_OWNED_BY_TEACHER]: 'The related session does not belong to this teacher.',
  [ROW_CODE.STUDENT_NOT_FOUND]: 'The related student was not found.',
  [ROW_CODE.DUPLICATE_REFERENCE_IN_FILE]:
    'Another row in this file has the same teacher and reference id.',
  [ROW_CODE.DUPLICATE_REFERENCE]: 'This reference id is already saved for this teacher and month.',
  [ROW_CODE.MONTH_CLOSED]: "This teacher's month is closed. Reopen it, then upload again.",
  [ROW_CODE.MONTH_NOT_OPEN]: "This teacher's month is not open.",
  [ROW_CODE.PREVIOUS_MONTH_NO_RECORD]: 'This teacher has no payment record for this month.',
  [ROW_CODE.CONFIRM_REQUIRED]: 'A large amount: confirm it with the tick above Apply.',
  [ROW_CODE.POSSIBLE_DUPLICATE]:
    'Looks like an entry already saved: confirm it with the tick above Apply.'
};

/*
 * D106/CD-113: a previous-month row for a teacher with no (or an empty) record is refused with R8's
 * pointer to the current month, named in full so Ops know exactly where the payment goes instead.
 */
const no_record_text = (row, ctx) => {
  const who = (row.teacher && row.teacher.name) || 'This teacher';
  return `${who} has no payment record for ${month_label(ctx.pay_month)}. Add this to ${month_label(
    ctx.current_month
  )} as a "Previous month payment adjustment" instead.`;
};

export const row_code_text = (c, row, ctx) => {
  if (c.code === ROW_CODE.PREVIOUS_MONTH_NO_RECORD && ctx.current_month)
    return no_record_text(row, ctx);
  return (
    c.message || ROW_CODE_TEXT[c.code] || 'This row has a problem the server did not describe.'
  );
};

const lookalike_text = (m) =>
  `Saved already: ${m.teacher_name || EM_DASH} · ${m.category_label || m.category} · ${amount_text(
    m.amount
  )} · added by ${who_view(m.added_by_name, m.added_by).text}${m.added_at_ist ? ` on ${m.added_at_ist}` : ''}`;

// ctx: { pay_month: the run's month, current_month: the IST month now } (both 'YYYY-MM[-DD]').
export const upload_row_view = (row, ctx) => {
  const verdict = VERDICT_VIEW[row.verdict] || { label: row.verdict || EM_DASH, tone: 'gray' };
  const t = row.teacher;
  return {
    key: String(row.line),
    line: row.line,
    verdict: row.verdict,
    verdict_label: verdict.label,
    verdict_tone: verdict.tone,
    teacher_name: t ? t.name || t.uuid : 'Not found',
    teacher_sub: t ? [t.email, MATCHED_BY_TEXT[t.matched_by]].filter(Boolean).join(' · ') : '',
    category_label: row.category_label || row.category || EM_DASH,
    amount_text: amount_text(row.amount),
    entry_text: row.entry_date
      ? `${row.entry_date}${row.entry_time ? ` ${row.entry_time}` : ''}`
      : EM_DASH,
    reference_id: row.reference_id || EM_DASH,
    note: row.note || '',
    issues: (Array.isArray(row.codes) ? row.codes : []).map((c) => ({
      code: c.code,
      text: row_code_text(c, row, ctx)
    })),
    lookalikes: (Array.isArray(row.lookalikes) ? row.lookalikes : []).map(lookalike_text)
  };
};

export const ROW_FILTER = { ALL: 'all', INVALID: 'invalid', CONFIRM: 'confirm' };

export const filter_rows = (rows, filter) => {
  if (filter === ROW_FILTER.INVALID) return rows.filter((r) => r.verdict === VERDICT.INVALID);
  if (filter === ROW_FILTER.CONFIRM) {
    return rows.filter((r) => r.verdict === VERDICT.NEEDS_CONFIRMATION);
  }
  return rows;
};

const who_at = (who, at) =>
  `${who_view(who && who.name, who && who.uuid).text}${at ? ` · ${at}` : ''}`;

// The three acknowledgements the apply needs, each only when its count is non-zero (R9-C4, D93).
export const needed_ticks = (counts) => {
  const c = counts || {};
  return [
    num(c.invalid) > 0 && {
      key: 'skip_invalid',
      label: `Skip the ${plural(num(c.invalid), 'invalid row')}`
    },
    num(c.confirm_large) > 0 && {
      key: 'confirm_large',
      label: `I have checked the ${plural(num(c.confirm_large), 'row')} with a large amount`
    },
    num(c.lookalike) > 0 && {
      key: 'confirm_lookalike',
      label: `I have checked the ${plural(num(c.lookalike), 'row')} that look${
        num(c.lookalike) === 1 ? 's' : ''
      } like an entry already saved`
    }
  ].filter(Boolean);
};

export const ticks_complete = (counts, ticks) =>
  needed_ticks(counts).every((t) => !!(ticks && ticks[t.key]));

export const run_view = (data, ctx) => {
  const d = data || {};
  const counts = d.counts || {};
  const status = RUN_STATUS_VIEW[d.status] || { label: d.status || EM_DASH, tone: 'gray' };
  const row_ctx = { pay_month: d.pay_month, current_month: ctx && ctx.current_month };
  return {
    run_uuid: d.run_uuid || '',
    status: d.status,
    status_label: status.label,
    status_tone: status.tone,
    month_text: month_label(d.pay_month),
    file_name: d.file_name || EM_DASH,
    uploaded_text: `Uploaded by ${who_at(d.uploaded_by, d.uploaded_at_ist)}`,
    counts: {
      total: num(counts.total),
      valid: num(counts.valid),
      invalid: num(counts.invalid),
      confirm_large: num(counts.confirm_large),
      lookalike: num(counts.lookalike)
    },
    can_apply: d.can_apply === true,
    // CD-210: the server says whether this viewer may apply or discard (the uploader or a SUPER_USER).
    can_act: d.can_act === true,
    not_yours_text:
      d.can_act === true
        ? ''
        : `Only ${who_view(d.uploaded_by && d.uploaded_by.name, d.uploaded_by && d.uploaded_by.uuid).text} can apply or discard this upload. Upload the file yourself to apply it.`,
    blocked_reason: d.apply_blocked_reason || '',
    applied_text: d.applied
      ? `Applied ${plural(num(d.applied.applied_rows), 'row')}, skipped ${plural(
          num(d.applied.skipped_rows),
          'invalid row'
        )} · by ${who_at(d.applied.by, d.applied.at_ist)}`
      : '',
    discarded_text: d.discarded
      ? `Discarded by ${who_at(d.discarded.by, d.discarded.at_ist)}${
          d.discarded.reason ? ` · ${d.discarded.reason}` : ''
        }`
      : '',
    last_apply_error: d.last_apply_error || '',
    ticks: needed_ticks(counts),
    rows: (Array.isArray(d.rows) ? d.rows : []).map((r) => upload_row_view(r, row_ctx)),
    teacher_names: (Array.isArray(d.rows) ? d.rows : []).reduce((m, r) => {
      if (r.teacher && r.teacher.uuid) m[r.teacher.uuid] = r.teacher.name || r.teacher.uuid;
      return m;
    }, {}),
    raw: d
  };
};

/*
 * After an apply: the teachers whose recompute did not finish, pending (budget spent) or failed. They
 * keep needs_recalc = 1 until each is recalculated through POST /recalc, one at a time (CD-33).
 */
export const recompute_remaining = (recompute) => {
  const r = recompute || {};
  const failed = (Array.isArray(r.done) ? r.done : []).filter((d) => d && d.ok === false);
  const pending = Array.isArray(r.pending) ? r.pending : [];
  return [...pending, ...failed.map((d) => d.teacher_uuid)].filter(
    (uuid, i, all) => uuid && all.indexOf(uuid) === i
  );
};

export const apply_result_view = (data) => {
  const d = data || {};
  const run = d.run || {};
  const applied = run.applied || {};
  const remaining = recompute_remaining(d.recompute);
  const head = d.replayed
    ? `This upload was already applied by ${who_at(applied.by, applied.at_ist)}. Nothing was applied twice.`
    : `Applied ${plural(num(applied.applied_rows), 'row')} to ${month_label(run.pay_month)}${
        num(applied.skipped_rows)
          ? `, skipped ${plural(num(applied.skipped_rows), 'invalid row')}`
          : ''
      }.`;
  const tail = remaining.length
    ? ` ${plural(remaining.length, 'teacher')} still ${
        remaining.length === 1 ? 'needs' : 'need'
      } recalculating; their months show "needs review" until then.`
    : '';
  return {
    replayed: d.replayed === true,
    tone: remaining.length ? 'warning' : 'success',
    message: `${head}${tail}`,
    remaining,
    warnings: (Array.isArray(d.warnings) ? d.warnings : [])
      .map((w) => (w && (w.message || w.code)) || '')
      .filter(Boolean)
  };
};

export const history_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r, i) => {
    const v = run_view(r, {});
    return {
      key: `${r.run_uuid}-${r.status}-${i}`,
      run_uuid: r.run_uuid,
      file_name: v.file_name,
      status: r.status,
      status_label: v.status_label,
      status_tone: v.status_tone,
      uploaded_text: who_at(r.uploaded_by, r.uploaded_at_ist),
      counts_text: `${plural(v.counts.total, 'row')} · ${fmt_count(v.counts.valid)} valid · ${fmt_count(
        v.counts.invalid
      )} invalid`,
      outcome_text: v.applied_text || v.discarded_text || ''
    };
  });
  return { rows, total_count: num(d.total_count) };
};

// A file the server would refuse by size alone is not sent (2 MB, D93); an empty one neither.
export const precheck_file = (file) => {
  if (!file) return '';
  if (file.size > UPLOAD_LIMITS.MAX_BYTES) {
    return 'The file is larger than 2 MB. Split it into smaller files and upload each one.';
  }
  if (file.size === 0) return 'The file is empty. Nothing was uploaded.';
  return '';
};
